import { makeAutoObservable } from 'mobx';
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  PointLight,
  AmbientLight,
  Vector2,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass';
import { BodyProcessingModel } from '../shared/models/sceneModels/BodyProcessingModel';

class SceneStore {
  public scene = new Scene();
  public camera: PerspectiveCamera;
  public renderer: WebGLRenderer;
  public controls: OrbitControls;
  public bodyModel: BodyProcessingModel | null = null;

  public composer: EffectComposer | null = null;
  public outlinePass: OutlinePass | null = null;

  private lights: PointLight[] = [];
  private mouseDownEvent!: (event: MouseEvent) => void;
  private mouseUpEvent!: (event: MouseEvent) => void;
  private mouseWheelEvent!: (event: MouseEvent) => void;

  public showButtons = true;
  public intersection: any[] = [];
  public isRotating = false;

  public isLoading = false;
  public hoverInfo: any = null;

  private isAnimating = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    // Камера
    this.camera = new PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.camera.position.set(0, 2, 5);

    // Рендерер
    this.renderer = new WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Контролы
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 0, 0);
    this.controls.update();

    // Свет
    const ambientLight = new AmbientLight(0xffffff, 0.7);
    this.scene.add(ambientLight);
    const pointLight = new PointLight(0xffffff, 1);
    pointLight.position.set(5, 5, 5);
    this.scene.add(pointLight);
    this.lights.push(pointLight);

    // Composer и OutlinePass
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.outlinePass = new OutlinePass(new Vector2(window.innerWidth, window.innerHeight), this.scene, this.camera);
    this.outlinePass.edgeStrength = 3;
    this.outlinePass.edgeGlow = 0.5;
    this.outlinePass.visibleEdgeColor.set('#ffffff');
    this.composer.addPass(this.outlinePass);

    // События мыши
    this.mouseDownEvent = () => (this.showButtons = false);
    this.mouseUpEvent = () => (this.showButtons = true);
    this.mouseWheelEvent = () => {
      this.showButtons = false;
      setTimeout(() => (this.showButtons = true), 200);
    };
    this.renderer.domElement.addEventListener('mousedown', this.mouseDownEvent);
    this.renderer.domElement.addEventListener('mouseup', this.mouseUpEvent);
    this.renderer.domElement.addEventListener('wheel', this.mouseWheelEvent);

    window.addEventListener('resize', () => this.onResize());
  }

  initScene(container: HTMLDivElement) {
    if (!container.contains(this.renderer.domElement)) {
      container.appendChild(this.renderer.domElement);
    }

    // ⚡ Создаём модель только один раз
    if (!this.bodyModel) {
      this.initModel();
    }

    // ⚡ Анимация запускается только один раз
    if (!this.isAnimating) {
      this.isAnimating = true;
      const animate = () => {
        requestAnimationFrame(animate);
        this.controls.update();
        if (this.composer) this.composer.render();
        else this.renderer.render(this.scene, this.camera);
      };
      animate();
    }
  }

  initModel() {
    if (!this.bodyModel) {
      this.bodyModel = new BodyProcessingModel(this);
      this.bodyModel.init(this.lights);
      if (this.bodyModel.modelData) this.scene.add(this.bodyModel.modelData);
    }
  }

  onResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    if (this.composer) this.composer.setSize(width, height);
    if (this.outlinePass) this.outlinePass.setSize(width, height);
  }

  addLight(light: PointLight) {
    this.lights.push(light);
    this.scene.add(light);
  }

  setCameraPosition(direction: string) {
    const rotateAmount = Math.PI / 2;
    const duration = 0.8; // секунды
    let startTime: number | null = null;

    const startRotY = this.controls.object.rotation.y;
    const startRotX = this.controls.object.rotation.x;

    let targetRotY = startRotY;
    let targetRotX = startRotX;

    if (direction === 'left') targetRotY -= rotateAmount;
    else if (direction === 'right') targetRotY += rotateAmount;
    else if (direction === 'top') targetRotX -= rotateAmount;
    else if (direction === 'bottom') targetRotX += rotateAmount;

    const animateRotation = (time: number) => {
      if (!startTime) startTime = time;
      const elapsed = (time - startTime) / 1000; // в секундах
      const progress = Math.min(elapsed / duration, 1);
      const easing = Math.sin((progress * Math.PI) / 2);

      this.controls.object.rotation.y = startRotY + (targetRotY - startRotY) * easing;
      this.controls.object.rotation.x = startRotX + (targetRotX - startRotX) * easing;

      this.controls.update();

      if (progress < 1) requestAnimationFrame(animateRotation);
    };

    requestAnimationFrame(animateRotation);
  }

  checkIntersection(mouse: Vector2) {
    if (!this.outlinePass || !this.bodyModel?.modelData) return;
  }

  fullDeInit() {
    // ⚡ Не удаляем модель, чтобы не было раздвоения
    if (this.renderer?.domElement) {
      this.renderer.domElement.removeEventListener('mousedown', this.mouseDownEvent);
      this.renderer.domElement.removeEventListener('mouseup', this.mouseUpEvent);
      this.renderer.domElement.removeEventListener('wheel', this.mouseWheelEvent);
    }
    this.composer?.dispose();
    this.renderer.dispose();
    this.isAnimating = false;
  }

  clearScene() {
    // ⚡ Удаляем все объекты кроме модели, камеры и света
    this.scene.children = this.scene.children.filter(
        (obj) =>
            obj === this.bodyModel?.modelData || obj.type === 'PerspectiveCamera' || obj.type.includes('Light')
    );
  }
}

export default new SceneStore();
